/**
 * The one piece of performance instrumentation the app carries (PLAN-LAUNCH E5).
 *
 * PLAN §10 budgets "<1 s cold start to interactive on repeat visit (local
 * data)", and measuring that needs a definition of *interactive* that the app
 * itself supplies. The browser's own timings cannot supply one: `load` and
 * `DOMContentLoaded` fire while the shell is still showing the opening splash,
 * `first-contentful-paint` is that splash, and `largest-contentful-paint` lands
 * wherever the heaviest text happens to be on whichever route was opened. None
 * of them is the moment the brewer can start work.
 *
 * That moment has a name in this codebase already: it is when `AppShell` stops
 * rendering `<OpeningSplash>` and renders the route, which happens when the
 * saved workspace has been read back out of IndexedDB. So the shell marks it,
 * and the budget check reads the mark.
 *
 * The mark is taken in a layout effect *after* React has committed that swap, so
 * it is a claim about the DOM rather than about a state variable — the same
 * reason the mark is not simply taken where `setHydrated(true)` is called.
 * Because the mark's timestamp is relative to the document's `timeOrigin`, its
 * `startTime` **is** the navigation-start-to-interactive figure; nothing has to
 * subtract anything.
 *
 * Cost in production: one `performance.mark` per document. It is not stripped
 * from the build, deliberately — an instrument that only exists under test
 * measures a program that only exists under test, and `PerformanceObserver` in
 * the field can read this same mark if real-user monitoring is ever wanted.
 */

/**
 * The mark name, and the contract with `e2e/perf-budget.spec.ts`.
 *
 * Namespaced so it cannot collide with a mark from a library or an extension,
 * and exported so the only two places that care — the shell that writes it and
 * the budget check that reads it — agree by construction rather than by
 * matching string literals.
 */
export const WORKSPACE_READY_MARK = 'beerwright:workspace-ready';

/**
 * Record that the workspace is on screen. Safe to call more than once.
 *
 * **Idempotent, and that matters.** React StrictMode double-invokes effects in
 * development, and `AppShell` remounts on an identity change; a second mark
 * would sit later in the timeline and quietly change what "interactive" means
 * for anything reading `getEntriesByName(…)[0]`. First writer wins, because the
 * first time the workspace appeared is the answer the budget is asking for.
 *
 * Guarded against a missing `performance` for the same reason every other
 * environment probe here is: this module is imported by the shell, which is
 * rendered by the jsdom suite, and instrumentation must never be the thing that
 * throws.
 */
export function markWorkspaceReady(): void {
  if (typeof performance === 'undefined' || typeof performance.mark !== 'function') return;
  if (performance.getEntriesByName(WORKSPACE_READY_MARK).length > 0) return;
  performance.mark(WORKSPACE_READY_MARK);
}
